import type { CatalogMenuItem } from '@store/CatalogStore.type';
import { Picture } from '@ui';
import type { FC, SyntheticEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import icons from '@icons';
import { observer } from 'mobx-react';
import { Link } from '@compat/router';
import type { CatalogTypesModel } from '@schema/cart';
import { twcls } from '@utils/twcls';
import BarMenuCategoryBadge from '@routes/BarMenu/components/BarMenuCategoryBadge';
const {
  flash: { red: flashRedFilled },
  pricetag: { midYellow: ptMYFilled },
  crab: { red: crabRedFilled }
} = icons.Fill;
const {
  chevronRight: { lightGrey: crLGOutlined, red: crRedOutlined }
} = icons.Outline;
interface CatalogMenuItemCompProps {
  item: CatalogMenuItem;
  activeIdentify: string;
  activeMenu: CatalogMenuItem;
  isNextItemActive?: boolean;
  isPrevItemActive?: boolean;
  onClick: (e: SyntheticEvent) => void;
  setActiveMenu: () => void;
}

export const CatalogMenuItemComp: FC<CatalogMenuItemCompProps> = observer(
  ({
    item,
    isPrevItemActive,
    isNextItemActive,
    activeIdentify,
    onClick,
    setActiveMenu
  }) => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
      () => () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      },
      []
    );
    const onMouseEnter = useCallback((): void => {
      timerRef.current = setTimeout(() => {
        setActiveMenu();
      }, 300);
    }, [setActiveMenu]);

    const onMouseLeave = useCallback((): void => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    }, []);

    const isActive = activeIdentify === item.identify;
    const className = twcls(
      `w-full
      cursor-pointer
      transition-colors
      py-3.5
      flex
      items-center
      hover:text-main-dark-red
      text-[var(--main-font-color)]
      `,
      isActive && 'text-main-dark-red'
    );
    const activateMenu = useCallback(
      (e: SyntheticEvent) => {
        e.stopPropagation();
        setActiveMenu();
      },
      [setActiveMenu]
    );
    if (item.isBarMenu) {
      return (
        <li
          key={item.id}
          className={twcls(
            `flex items-center border-0
            border-solid
            border-t
            border-b
            first-of-type:border-t-0
            last-of-type:border-b-0
            border-[var(--light-grey)]
            hover:rounded-lg
            hover:font-medium
            hover:text-[var(--dark-red)]
            hover:border-[var(--light-red)]
            hover:bg-[#E0F4FF]
            gap-2
            p-1
            select-none
            pr-0
            `,
            isActive &&
              'rounded-lg font-medium text-[var(--dark-red)] border-[var(--light-red)] bg-[#E0F4FF]',
            isNextItemActive && 'border-b border-b-white',
            isPrevItemActive && 'border-t border-t-white',
            item.isHidden && 'hidden'
          )}
          onMouseEnter={
            item.identify !== activeIdentify ? onMouseEnter : undefined
          }
          onMouseLeave={onMouseLeave}
        >
          <Link
            to={item.url}
            onClick={onClick}
            className={twcls(
              `w-full
              gap-2
              cursor-pointer
              transition-colors
              p-1
              pr-0
              flex
              items-center
              hover:text-main-dark-red
              text-[var(--main-font-color)]
              `,
              isActive && 'text-[var(--dark-red)]'
            )}
          >
            {item.icon && <Picture icon={item.icon} width={44} height={44} />}
            <span className='text-base '>{item.title}</span>
          </Link>
          <Picture
            onClick={activateMenu}
            pictureClassName={'ml-auto'}
            icon={isActive ? crRedOutlined : crLGOutlined}
            width='20'
            height='20'
          />
        </li>
      );
    }
    return (
      <li
        onMouseEnter={
          item.identify !== activeIdentify ? onMouseEnter : undefined
        }
        onMouseLeave={onMouseLeave}
        className={twcls(
          `flex items-center border-0
      border-solid
      border-t
      border-b
      first-of-type:border-t-0
      last-of-type:border-b-0
      border-[var(--light-grey)]
      hover:rounded-lg
      hover:pl-3.5
      hover:font-medium
      hover:text-[var(--dark-red)]
      hover:border-[var(--light-red)]
      hover:bg-[var(--light-red)]
      `,
          isActive &&
            'rounded-lg pl-3.5 font-medium text-[var(--dark-red)] border-[var(--light-red)] bg-[var(--light-red)]',
          isNextItemActive && 'border-b border-b-white',
          isPrevItemActive && 'border-t border-t-white'
        )}
      >
        <Link to={item.url} onClick={onClick} className={className}>
          {item.icon && (
            <Picture
              className='flex-shrink min-w-[1.5rem] block mr-3'
              icon={item.icon}
              width='24'
              height='24'
              alt={`иконка-${item.title}`}
            />
          )}
          {item.title}
        </Link>
        <Picture
          onClick={activateMenu}
          pictureClassName={'ml-auto'}
          icon={isActive ? crRedOutlined : crLGOutlined}
          width='20'
          height='20'
        />
      </li>
    );
  }
);

interface MenuListProps {
  list: CatalogMenuItem[] | CatalogTypesModel[];
  activeMenu?: CatalogMenuItem;
  activeSubMenu?: CatalogTypesModel | null;
  selectSubMenu?: (item: CatalogMenuItem | null) => void;
  onClick: (e: SyntheticEvent) => void;
  className?: string;
}

export const MenuListBarMenu: FC<MenuListProps> = observer(
  ({ list, onClick, className }) => {
    if (!list?.length) {
      return null;
    }
    return (
      <div className='w-full h-full max-h-full overflow-hidden '>
        <ul
          className={twcls(
            'w-full max-h-full overflow-auto custom-scroll custom-scroll-mini pr-2 grid grid-cols-3 gap-2',
            className
          )}
        >
          {list.map(item => (
            <li key={item.identify} className='relative w-full h-full text-sm'>
              <Link
                className={twcls(
                  `relative
                  flex
                  flex-col
                  items-center
                  justify-center
                  rounded-xl
                  p-1
                  text-center
                  aspect-square
                  bg-[#F1F1F1] 
                  transition-colors
                  hover:bg-[var(--ultra-light-grey)]
                  hover:text-main-dark-red`,
                  item.isActive && '  text-main-dark-red'
                )}
                style={
                  item.bg_color ? { backgroundColor: item.bg_color } : undefined
                }
                onClick={onClick}
                to={item.url}
              >
                {!!item.badge_text && (
                  <BarMenuCategoryBadge text={item.badge_text} />
                )}
                {item.icon && (
                  <Picture
                    icon={item.icon}
                    width={104}
                    height={94}
                    className='mb-4'
                  />
                )}
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }
);

export const MenuList: FC<MenuListProps> = observer(
  ({ list, activeMenu, selectSubMenu, onClick, activeSubMenu }) => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
      () => () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      },
      []
    );

    const onMouseEnter = useCallback(
      (item: CatalogMenuItem | null): void => {
        if (!item?.childrenItems) {
          if (selectSubMenu) {
            selectSubMenu(null);
          }
          return;
        }
        if (selectSubMenu) {
          timerRef.current = setTimeout(() => {
            selectSubMenu(item);
          }, 300);
        }
      },
      [selectSubMenu]
    );

    const onMouseLeave = useCallback((): void => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    }, []);

    return (
      <div className='w-full h-full max-h-full overflow-hidden '>
        <ul
          className={
            'w-full max-h-full overflow-auto custom-scroll custom-scroll-mini pr-2 '
          }
        >
          {!!list?.length &&
            list?.map(item => (
              <li
                key={item.identify}
                onMouseLeave={onMouseLeave}
                onMouseEnter={
                  activeMenu?.identify !== item?.identify ||
                  activeSubMenu?.identify !== item?.identify
                    ? onMouseEnter.bind(null, item)
                    : undefined
                }
                className={twcls(`
              flex
              flex-col
              justify-center
                `)}
              >
                <div className='flex items-center'>
                  <Link
                    className={twcls(
                      `
                  flex
                  items-center
                  w-full
                  transition-colors
                  p-2.5
                  hover:text-main-dark-red`,
                      item.isActive && '  text-main-dark-red'
                    )}
                    onClick={onClick}
                    to={item.url}
                  >
                    {item.title}
                    {item.identify === 'newbee' && (
                      <Picture
                        pictureClassName={'ml-1.5'}
                        icon={flashRedFilled}
                        height='20'
                        width='20'
                      />
                    )}
                    {item.identify === 'delikateska' && (
                      <Picture
                        pictureClassName={'ml-1.5'}
                        icon={crabRedFilled}
                        height='20'
                        width='20'
                      />
                    )}
                    {item.identify === 'promo' && (
                      <Picture
                        pictureClassName={'ml-1.5'}
                        icon={ptMYFilled}
                        height='20'
                        width='20'
                      />
                    )}
                    {!!item?.rubticProductsCount &&
                      item.rubticProductsCount > 0 && (
                        <span className='ml-1.5 text-[#c2c2c2]'>
                          {item.rubticProductsCount}
                        </span>
                      )}
                  </Link>
                </div>
              </li>
            ))}
        </ul>
      </div>
    );
  }
);

MenuList.displayName = 'MenuList';
